import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { FlamingoAuthService } from '@flamingo/service/flamingo-auth.service';
import { MainService } from './core/services/main.service';

export function initializeApp(): () => Promise<void> {
    const authService = inject(FlamingoAuthService);
    const mainService = inject(MainService);
    const router = inject(Router);

    return () =>
        new Promise<void>(resolve => {
            const token = authService.getToken();

            // If no token, resolve immediately (user will be redirected to login by guard)
            if (!token) {
                console.log('[initializeApp] No token found, resolving');
                resolve();
                return;
            }

            // Check if token is expired
            if (!authService.isTokenValid()) {
                console.warn('[initializeApp] Token is expired, clearing and resolving');
                authService.logout();
                resolve();
                return;
            }

            // Add timeout to prevent infinite loading
            const timeout = setTimeout(() => {
                console.warn('[initializeApp] Timeout reached, resolving anyway');
                resolve();
            }, 5000);

            // Validate token by getting account
            // Note: We only logout if token is expired or API returns 401
            // If getAccount() returns null or fails for other reasons, we keep the token
            // as it might be a temporary API issue
            mainService.getAccount().subscribe({
                next: account => {
                    clearTimeout(timeout);
                    // Don't logout if account is null - token is still valid
                    // The guard will handle navigation if needed
                    if (account) {
                        console.log('[initializeApp] Account loaded successfully');
                    } else {
                        console.warn('[initializeApp] No account found, but token is valid - keeping session');
                    }
                    resolve();
                },
                error: (error) => {
                    clearTimeout(timeout);
                    console.warn('[initializeApp] Error getting account:', error);
                    // Only logout if 401 (unauthorized) or token is actually invalid
                    // Other errors (network, 500, etc.) don't invalidate the token
                    if (error.status === 401) {
                        console.warn('[initializeApp] 401 Unauthorized - token invalid, logging out');
                        authService.logout();
                    } else if (!authService.isTokenValid()) {
                        console.warn('[initializeApp] Token is invalid, logging out');
                        authService.logout();
                    } else {
                        console.log('[initializeApp] Error getting account but token is valid - keeping session');
                    }
                    resolve();
                }
            });
        });
}


